<script setup lang="ts">
interface Props {
  wl?: number
  amp?: number
  th?: number
  color?: string
  speed?: number
  orientation?: 'horizontal' | 'vertical'
  length?: string
  phase?: number  // px; when set, disables CSS animation and pins position to this value
}

const props = withDefaults(defineProps<Props>(), {
  wl: 32,
  amp: 4,
  th: 2,
  color: '#111827',
  speed: 2,
  orientation: 'horizontal',
  length: '120px',
})

const tileThickness = computed(() => props.amp * 2 + props.th)

function encode(svg: string) {
  return encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22')
}

const horizontalSvg = computed(() => {
  const { wl, amp, th, color } = props
  const h = tileThickness.value
  const mid = h / 2
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${wl}' height='${h}' viewBox='0 0 ${wl} ${h}'><path d='M0 ${mid} Q${wl * 0.25} ${mid - amp} ${wl * 0.5} ${mid} Q${wl * 0.75} ${mid + amp} ${wl} ${mid}' stroke='${color}' stroke-width='${th}' fill='none' stroke-linecap='round'/></svg>`
})

const verticalSvg = computed(() => {
  const { wl, amp, th, color } = props
  const w = tileThickness.value
  const mid = w / 2
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${wl}' viewBox='0 0 ${w} ${wl}'><path d='M${mid} 0 Q${mid - amp} ${wl * 0.25} ${mid} ${wl * 0.5} Q${mid + amp} ${wl * 0.75} ${mid} ${wl}' stroke='${color}' stroke-width='${th}' fill='none' stroke-linecap='round'/></svg>`
})

const hasPhase = computed(() => props.phase !== undefined)

const cssVars = computed(() => {
  const isH = props.orientation === 'horizontal'
  const vars: Record<string, string> = {
    '--wl-wl': `${props.wl}px`,
    '--wl-th': `${tileThickness.value}px`,
    '--wl-speed': `${props.speed}s`,
    '--wl-wl-neg': `-${props.wl}px`,
    '--wl-length': props.length,
    '--wl-bg': isH
      ? `url("data:image/svg+xml,${encode(horizontalSvg.value)}")`
      : `url("data:image/svg+xml,${encode(verticalSvg.value)}")`,
  }
  if (hasPhase.value) {
    vars['--wl-phase'] = `${props.phase}px`
  }
  return vars
})
</script>

<template>
  <span :class="['wavy-line', orientation, { 'no-anim': hasPhase }]" :style="cssVars" aria-hidden="true" />
</template>

<style scoped>
.wavy-line {
  display: inline-block;
  pointer-events: none;
  vertical-align: middle;
}

.wavy-line.horizontal {
  width: var(--wl-length);
  height: var(--wl-th);
  background-image: var(--wl-bg);
  background-size: var(--wl-wl) var(--wl-th);
  background-repeat: repeat-x;
  animation: wl-slide-h var(--wl-speed) linear infinite;
}

.wavy-line.no-anim.horizontal {
  animation: none;
  background-position-x: var(--wl-phase, 0px);
}

.wavy-line.no-anim.vertical {
  animation: none;
  background-position-y: var(--wl-phase, 0px);
}

.wavy-line.vertical {
  width: var(--wl-th);
  height: var(--wl-length);
  background-image: var(--wl-bg);
  background-size: var(--wl-th) var(--wl-wl);
  background-repeat: repeat-y;
  animation: wl-slide-v var(--wl-speed) linear infinite;
}
</style>

<style>
@keyframes wl-slide-h {
  from { background-position-x: 0; }
  to   { background-position-x: var(--wl-wl-neg); }
}

@keyframes wl-slide-v {
  from { background-position-y: 0; }
  to   { background-position-y: var(--wl-wl-neg); }
}
</style>
